import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Button, Modal, Text, useTheme } from '@/components/ui';
import { spacing } from '@/styles/theme';

/**
 * "Discard unsaved changes?" confirm shown when the user tries to close a form
 * modal that has dirty state.
 *
 * Built on the canonical `@/components/ui` Modal (which wraps RN's Modal) so it
 * sits ON TOP of the parent form modal. `Alert.alert` / `ConfirmDialog` both
 * render through react-native-paper's `Portal`, which paints UNDERNEATH an
 * RNModal-based modal on web — the confirm would be invisible and unreachable,
 * leaving the parent modal impossible to close (issue #349). Mount this as a
 * sibling inside the parent modal's tree instead.
 */
interface DiscardChangesConfirmProps {
	visible: boolean;
	onKeepEditing: () => void;
	onDiscard: () => void;
	title?: string;
	message?: string;
	discardLabel?: string;
}

export const DiscardChangesConfirm: React.FC<DiscardChangesConfirmProps> = ({
	visible,
	onKeepEditing,
	onDiscard,
	title = 'Discard changes?',
	message = 'You have unsaved changes. Close anyway?',
	discardLabel = 'Discard',
}) => {
	const theme = useTheme();
	return (
		<Modal visible={visible} onDismiss={onKeepEditing} contentContainerStyle={styles.modal}>
			<Text style={[styles.title, { color: theme.colors.onSurface }]}>{title}</Text>
			<Text style={[styles.message, { color: theme.colors.onSurfaceVariant }]}>{message}</Text>
			<View style={styles.actions}>
				<Button mode="outlined" onPress={onKeepEditing} testID="discard-changes-keep">
					Keep editing
				</Button>
				<Button mode="contained" buttonColor={theme.colors.error} onPress={onDiscard} testID="discard-changes-discard">
					{discardLabel}
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: 420, maxWidth: '95%' },
	title: { fontSize: 18, fontWeight: '700', marginBottom: spacing.s },
	message: { fontSize: 14, lineHeight: 20, marginBottom: spacing.m },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: spacing.s },
});
